// @ts-strict-ignore
import { useTranslation } from "react-i18next";

import { Arrange, Typography, Link } from "@aviary/components";
import { l } from "@aviary/locales/i18n";
import { sharedRoutes } from "@shared/data/sharedRoutes";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";

interface Props {
  type?: "basic" | "product" | "checkout" | "labs";
  isWholesale?: boolean;
}

const FooterLinkList = ({ isWholesale, type = "basic" }: Props) => {
  const { t } = useTranslation();
  const { isUS } = useSharedGlobalConfig();
  const { desktop } = useBreakpoints();
  const { isPatient } = useSharedGlobalConfig();

  const getShippingPolicyLink = () => {
    return isWholesale
      ? sharedRoutes.external.shippingPolicyWholesale
      : sharedRoutes.external.shippingPolicyRetail;
  };

  const getTermsOfServiceLink = () => {
    return isPatient
      ? sharedRoutes.external.termsOfServicePatients
      : sharedRoutes.external.termsOfService;
  };

  const renderPrivacyRightsNotice = () => {
    if (isUS) {
      return (
        <Typography type="footnote">
          <Link
            isColor="system"
            noExternalIcon
            isHoverUnderLined
            isUnderLined={false}
            href={sharedRoutes.external.privacyRightsNotice}
          >
            {t(l.aviary.footer.PrivacyRightsNotice)}
          </Link>
        </Typography>
      );
    }
  };

  const renderShippingPolicy = () => {
    if (type !== "checkout") {
      return null;
    }

    return (
      <Typography type="footnote">
        <Link
          isHoverUnderLined
          isColor="system"
          noExternalIcon
          isUnderLined={false}
          href={getShippingPolicyLink()}
        >
          {t(l.aviary.footer.ShippingPolicy)}
        </Link>
      </Typography>
    );
  };

  const renderRefundPolicy = () => {
    if (type !== "checkout") {
      return null;
    }

    return (
      <Typography type="footnote">
        <Link
          isColor="system"
          noExternalIcon
          isHoverUnderLined
          isUnderLined={false}
          href={sharedRoutes.external.refundPolicy}
        >
          {t(l.aviary.footer.RefundPolicy)}
        </Link>
      </Typography>
    );
  };

  const renderAccessibilityPolicy = () => {
    if (type === "checkout" || isUS) {
      return null;
    }

    return (
      <Typography type="footnote">
        <Link
          isColor="system"
          noExternalIcon
          isHoverUnderLined
          isUnderLined={false}
          href={sharedRoutes.external.accessibility}
        >
          {t(l.aviary.footer.AccessibilityPolicy)}
        </Link>
      </Typography>
    );
  };

  return (
    <Arrange isVertical={desktop.lessThan} alignment={desktop.lessThan ? "center" : null}>
      <Typography type="footnote">
        <Link
          isColor="system"
          noExternalIcon
          isHoverUnderLined
          isUnderLined={false}
          href={getTermsOfServiceLink()}
        >
          {t(l.aviary.footer.TermsOfService)}
        </Link>
      </Typography>
      <Typography type="footnote">
        <Link
          isColor="system"
          noExternalIcon
          isHoverUnderLined
          isUnderLined={false}
          href={sharedRoutes.external.privacyPolicy}
        >
          {t(l.aviary.footer.PrivacyStatement)}
        </Link>
      </Typography>
      {renderAccessibilityPolicy()}
      {renderPrivacyRightsNotice()}
      {renderShippingPolicy()}
      {renderRefundPolicy()}
      <Typography type="footnote">
        <Link
          isColor="system"
          noExternalIcon
          isHoverUnderLined
          isUnderLined={false}
          href={sharedRoutes.external.support}
        >
          {t(l.aviary.footer.HelpAndSupport)}
        </Link>
      </Typography>
    </Arrange>
  );
};

export { FooterLinkList };
